import { createBlock } from '../../../utils/blockUtils';
import { focusEditable } from '../../../utils/focusUtils';
import { stripHtml } from '../../../utils/caretUtils';
import { startPropFromTyped } from '../../../utils/indentUtils';
import { matchLanguage } from '../../CodeBlock';

/**
 * Markdown-style conversions checked against the raw typed text
 * (nbsp-normalised, since contenteditable renders spaces as &nbsp;).
 * Returns true when the paragraph was converted (caller should stop).
 */
export const tryApplyMarkdownShortcuts = ({
  value,
  prevValue,
  block,
  onUpdate,
  onUpdateAndInsert,
}) => {
  const typed = stripHtml(value).replace(/\u00a0/g, ' ');
  const prevTyped = stripHtml(prevValue).replace(/\u00a0/g, ' ');

  // "---" converts to a divider (with a fresh paragraph after it)
  if (typed.trim() === '---') {
    onUpdateAndInsert(
      block.id,
      { type: 'divider', content: '', props: {} },
      createBlock('text')
    );
    return true;
  }

  // "```" (optionally "```lang") at the start of a paragraph converts to a
  // code block. Only fires when the marker was just completed.
  if (/^```/.test(typed) && !/^```/.test(prevTyped)) {
    const lang = typed.slice(3).trim();
    onUpdate(block.id, {
      type: 'code',
      content: '',
      props: {
        language: matchLanguage(lang) || 'plaintext',
        wrapText: true,
        showLineNumbers: false,
      },
    });
    setTimeout(() => {
      document
        .querySelector(
          `[data-block-id="${block.id}"] .code-block-editable, [data-code-block="${block.id}"]`
        )
        ?.focus();
    }, 0);
    return true;
  }

  // "[]" or "[ ]" at the start of a paragraph converts to a checkbox —
  // text after the marker is kept as the checkbox content. Only fires when
  // the marker was just completed, so loaded lines starting with "[]" are
  // left alone.
  if (/^\[\s?\]/.test(typed) && !/^\[\s?\]/.test(prevTyped)) {
    const remainder = value.replace(/^\[(\s|&nbsp;|\u00a0)?\]/, '');
    onUpdate(block.id, {
      type: 'checkbox',
      content: remainder,
      props: { ...block.props, checked: false },
    });
    focusEditable(block.id, { atStart: true });
    return true;
  }

  // "- " (or "* ") at the start of a paragraph converts to a bullet point —
  // also in front of EXISTING text, which is kept as the bullet's content.
  // Only fires when the marker was just typed (previous content didn't
  // start with it), so loaded text beginning with "- " is left alone.
  if (/^[-*] /.test(typed) && !/^[-*] /.test(prevTyped)) {
    const remainder = value.replace(/^[-*](&nbsp;|\u00a0| )/, '');
    onUpdate(block.id, { type: 'bullet', content: remainder, props: { ...block.props } });
    focusEditable(block.id, { atStart: true });
    return true;
  }

  // "1. " (any digits) at the start converts to a numbered list item.
  // Digits other than 1 are kept as props.start so the marker is not reset.
  if (/^\d+\. /.test(typed) && !/^\d+\. /.test(prevTyped)) {
    const remainder = value.replace(/^\d+\.(&nbsp;|\u00a0| )/, '');
    onUpdate(block.id, {
      type: 'number',
      content: remainder,
      props: { ...block.props, ...startPropFromTyped(typed) },
    });
    focusEditable(block.id, { atStart: true });
    return true;
  }

  // "> " at the start converts to a quote (callout variant)
  if (/^> /.test(typed) && !/^> /.test(prevTyped)) {
    const remainder = value.replace(/^>(&nbsp;|\u00a0| )/, '');
    const child = createBlock('text', remainder);
    onUpdate(block.id, {
      type: 'callout',
      content: '',
      props: { icon: '❝', variant: 'quote' },
      children: [child],
    });
    focusEditable(child.id, { atStart: true });
    return true;
  }

  return false;
};
